import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoCreateOutline,
  IoOpenOutline,
} from "react-icons/io5";
import { listPages } from "../../../api/pagesApi";
import { listSections } from "../../../api/sectionsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import {
  CMS_INDUSTRY_PAGES,
  INDUSTRY_SECTION_LABELS,
} from "../../../config/cmsIndustryPages";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";

export default function IndustryPagesManager() {
  const [pages, setPages] = useState([]);
  const [sectionCounts, setSectionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listPages();
      const allPages = Array.isArray(data) ? data : [];
      setPages(allPages);

      const industryPages = CMS_INDUSTRY_PAGES.map((meta) =>
        allPages.find((p) => p.slug === meta.slug)
      ).filter(Boolean);

      const counts = {};
      await Promise.all(
        industryPages.map(async (page) => {
          try {
            const sections = await listSections(page.id);
            counts[page.slug] = Array.isArray(sections) ? sections.length : 0;
          } catch {
            counts[page.slug] = 0;
          }
        })
      );
      setSectionCounts(counts);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pagesBySlug = useMemo(() => {
    const map = {};
    pages.forEach((page) => {
      map[page.slug] = page;
    });
    return map;
  }, [pages]);

  if (loading) return <LoadingState message="Loading industry pages…" />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  const readyCount = CMS_INDUSTRY_PAGES.filter((meta) => pagesBySlug[meta.slug]).length;

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-sky-200/60 bg-gradient-to-br from-sky-50 via-white to-slate-50 p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
          Industries
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">
          Industry pages
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
          Edit banner, challenges, capabilities, and &ldquo;What this enables&rdquo; content
          for each industry vertical.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-medium text-sky-800">
          <span className="h-2 w-2 rounded-full bg-sky-500" />
          {readyCount} of {CMS_INDUSTRY_PAGES.length} pages ready
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {CMS_INDUSTRY_PAGES.map((meta) => {
          const page = pagesBySlug[meta.slug];
          const count = sectionCounts[meta.slug];

          return (
            <article
              key={meta.slug}
              className={`group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${meta.accentBorder}`}
            >
              <div className={`bg-gradient-to-br ${meta.accent} px-5 py-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${meta.accentDot}`} />
                      <h3 className="text-lg font-semibold text-slate-900">
                        {meta.name}
                      </h3>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{meta.description}</p>
                  </div>
                  {page ? (
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                        page.isPublished
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {page.isPublished ? "Live" : "Draft"}
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Missing
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-1 flex-col px-5 py-4">
                <div className="mb-4 flex flex-wrap gap-1.5">
                  {INDUSTRY_SECTION_LABELS.map((section) => (
                    <span
                      key={section.key}
                      className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600"
                    >
                      {section.label}
                    </span>
                  ))}
                </div>

                <div className="mt-auto space-y-3 border-t border-slate-100 pt-4">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{meta.publicPath}</span>
                    {page && count != null ? (
                      <span>{count} section{count === 1 ? "" : "s"}</span>
                    ) : null}
                  </div>

                  {page ? (
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to={`/cmsadmin/dashboard/pages/${page.id}/sections`}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
                      >
                        <IoCreateOutline className="h-4 w-4" />
                        Edit content
                      </Link>
                      <a
                        href={meta.publicPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        <IoOpenOutline className="h-4 w-4" />
                        View
                      </a>
                    </div>
                  ) : (
                    <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      Page not found in CMS. Restart the backend to run seeds.
                    </p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {readyCount === 0 ? (
        <EmptyState
          title="No industry pages seeded yet"
          description="Run the backend seeder to create Mobility, Healthcare, FinTech, and other industry pages."
        />
      ) : null}
    </div>
  );
}
